// Sensor API wrappers.

import type {
  PlacedSensor,
  Sensor,
  SensorAlert,
  SensorReadingsSeries,
  SensorsFeatureFlagResponse,
  SensorWithLatest,
} from '@greeniq/shared';
import { api } from './client.js';

export interface SensorsListResponse {
  sensors: SensorWithLatest[];
}

export function getCourseSensors(courseSlug: string): Promise<SensorsListResponse> {
  return api.get<SensorsListResponse>(`/courses/${courseSlug}/sensors`);
}

export interface ZoneSensorsResponse {
  sensors: SensorWithLatest[];
  state: {
    vwc2In: string | null;
    vwc4In: string | null;
    soilTempF: string | null;
    lastTickAt: string | null;
  } | null;
}

export function getZoneSensors(
  courseSlug: string,
  zoneId: number,
): Promise<ZoneSensorsResponse> {
  return api.get<ZoneSensorsResponse>(`/courses/${courseSlug}/zones/${zoneId}/sensors`);
}

export interface SensorReadingsResponse {
  series: SensorReadingsSeries[];
}

export function getSensorReadings(
  courseSlug: string,
  sensorId: number,
  window: '24h' | '7d' = '24h',
): Promise<SensorReadingsResponse> {
  return api.get<SensorReadingsResponse>(
    `/courses/${courseSlug}/sensors/${sensorId}/readings?window=${window}`,
  );
}

export function getSensorsFeatureFlag(courseSlug: string): Promise<SensorsFeatureFlagResponse> {
  return api.get<SensorsFeatureFlagResponse>(`/courses/${courseSlug}/sensors/feature-flag`);
}

export interface SeedDemoSensorsResponse {
  courseId: number;
  zonesTouched: number;
  sensorsInserted: number;
  sensorsExisting: number;
}

export function seedDemoSensors(courseSlug: string): Promise<SeedDemoSensorsResponse> {
  return api.post<SeedDemoSensorsResponse>(`/courses/${courseSlug}/seed-demo-sensors`);
}

export interface SensorPlacementsResponse {
  placements: PlacedSensor[];
}

export function getSensorPlacements(courseSlug: string): Promise<SensorPlacementsResponse> {
  return api.get<SensorPlacementsResponse>(`/courses/${courseSlug}/sensors/placements`);
}

export interface SensorAlertsResponse {
  alerts: SensorAlert[];
}

export function getSensorAlerts(
  courseSlug: string,
  status: 'unacknowledged' | 'acknowledged' | 'dismissed' | 'all' = 'unacknowledged',
  limit = 10,
): Promise<SensorAlertsResponse> {
  return api.get<SensorAlertsResponse>(
    `/courses/${courseSlug}/sensors/alerts?status=${status}&limit=${limit}`,
  );
}

export function acknowledgeSensorAlert(
  courseSlug: string,
  alertId: number,
): Promise<{ alert: SensorAlert }> {
  return api.post<{ alert: SensorAlert }>(
    `/courses/${courseSlug}/sensors/alerts/${alertId}/acknowledge`,
  );
}

export function updateSensorPlacement(
  courseSlug: string,
  sensorId: number,
  lng: number,
  lat: number,
): Promise<{ id: number; lng: number; lat: number }> {
  return api.patch<{ id: number; lng: number; lat: number }>(
    `/courses/${courseSlug}/sensors/${sensorId}/placement`,
    { lng, lat },
  );
}

export type { Sensor };
